import React from 'react';
import { Container, createRoot } from 'react-dom/client';
import 'core-js/stable';
import WidgetApp from './WidgetApp';
import './styles/index.css';
import './utils/i18n';
import initSentry, {
  SENTRY_SPANS,
  executeWithSentrySpan,
  reactErrorHandler,
} from './utils/sentry';

// Function to dynamically load CSS
const loadCSS = (href: string) => {
  return new Promise<void>((resolve, reject) => {
    // Check if CSS is already loaded
    const existingLink = document.querySelector(`link[href="${href}"]`);
    if (existingLink) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
    document.head.appendChild(link);
  });
};

// Function to get the base URL of the current script
const getScriptBaseURL = () => {
  const scripts = document.querySelectorAll('script[src*="widget.js"]');
  const currentScript = scripts[scripts.length - 1] as HTMLScriptElement;
  if (currentScript && currentScript.src) {
    return currentScript.src.replace(/\/[^/]*$/, '/');
  }
  return '';
};

// Enhanced widget initialization function with comprehensive tracing
const initializeWidget = async () => {
  return executeWithSentrySpan(
    SENTRY_SPANS.WIDGET_INIT,
    async () => {
      const baseURL = getScriptBaseURL();

      // CSS Loading with sub-span
      await executeWithSentrySpan(
        SENTRY_SPANS.CSS_LOAD,
        async () => {
          if (baseURL) {
            await loadCSS(`${baseURL}widget.css`);
          }
        },
        {
          customErrorMessage:
            'SleekFlow Widget: CSS loading failed, styles may not display correctly',
          shouldThrow: false, // Don't fail the entire widget if CSS fails
        },
      );

      // DOM Creation with sub-span
      await executeWithSentrySpan(SENTRY_SPANS.DOM_CREATE, async () => {
        // Create widget container
        const widgetContainer = document.createElement('div');
        widgetContainer.id = 'sleekflow-widget-container';

        const shadowRoot = widgetContainer.attachShadow({ mode: 'open' });

        // NOTE: even with Shadow DOM, certain CSS properties are inherited and will pass through the Shadow boundary
        // To prevent inheritance for all properties within the Shadow DOM, set all: initial on :host
        shadowRoot.innerHTML = `
            <style>
              @import url('${import.meta.env.DEV ? '/src/styles/index.css' : `${baseURL}widget.css`}');

              :host {
                all: initial;
              }

              * {
                font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                box-sizing: border-box;
              }
            </style>
          `;

        // Append to body
        document.body.appendChild(widgetContainer);

        // Mount React app with sub-span
        await executeWithSentrySpan(SENTRY_SPANS.REACT_MOUNT, async () => {
          const root = createRoot(shadowRoot as unknown as Container, {
            onUncaughtError: reactErrorHandler((error, errorInfo) => {
              console.warn('Uncaught error', error, errorInfo.componentStack);
            }),
            onCaughtError: reactErrorHandler(),
            onRecoverableError: reactErrorHandler(),
          });
          root.render(React.createElement(WidgetApp));
        });
      });
    },
    {
      customErrorMessage: 'SleekFlow Widget: Initialization failed',
    },
  );
};

initSentry();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWidget);
} else {
  initializeWidget();
}
